
import React from "react";
import { Badge } from "reactstrap";

// Replace the BidNumberBadge component
export const BidNumberBadge = ({ number, amount, isWinning }) => (
    <div
        className={`d-flex flex-column align-items-center justify-content-center ${isWinning ? 'bg-success' : 'bg-light'} border rounded-3 p-1`}
        style={{
            width: '20px',
            height: '20px',
            fontSize: '10px',
            fontWeight: '600',
            boxShadow: isWinning ? '0 2px 4px rgba(25, 135, 84, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
            position: 'relative'
        }}
        title={`${number}: ${amount || 0}`}
    >
        <span className={`${isWinning ? 'text-white' : 'text-dark'}`}>
            {number}
        </span>
    </div>
);

export const ROBetDisplay = ({ bet, isWinningRow, winningNumber }) => {
    const { betType, betValues, amount, symbol, bidNumber } = bet;

    const renderValues = () => {
        if (betType === 'SYMBOL' && (symbol !== undefined || bidNumber !== undefined)) {
            return symbol !== undefined ? symbol : bidNumber;
        }
        if (!betValues || !Array.isArray(betValues)) return 'N/A';
        if (betType === 'DOZEN') {
            return betValues[0]?.replace('_', '-') || 'N/A';
        }
        if (betType === 'COLUMN') {
            return betValues[0]?.replace('COL_', 'Col ') || 'N/A';
        }
        return betValues.join(', ');
    };

    const isWinningBet = isWinningRow && winningNumber !== undefined && winningNumber !== null && (
        betValues?.includes(winningNumber) || 
        symbol == winningNumber || 
        bidNumber == winningNumber
    );


    return (
        <Badge
            color={isWinningBet ? "success" : "light"}
            className="p-1 px-2 border d-inline-flex flex-column align-items-start mb-1 me-1 shadow-sm"
            style={{ minWidth: '65px', borderRadius: '4px' }}
        >
            <div className="d-flex justify-content-between w-100 gap-2 align-items-center mb-1">
                <small
                    className={`${isWinningBet ? 'text-white' : 'text-muted'} fw-normal`}
                    style={{ fontSize: '7px', textTransform: 'uppercase', letterSpacing: '0.2px', opacity: isWinningBet ? 0.9 : 0.8 }}
                >
                    {betType}
                </small>
                <small
                    className={`${isWinningBet ? 'text-white' : 'text-primary'} fw-bold`}
                    style={{ fontSize: '9px' }}
                >
                    {amount}
                </small>
            </div>
            <div
                className={isWinningBet ? "text-white" : "text-dark"}
                style={{ fontSize: '10.5px', fontWeight: '700', lineHeight: '1.2' }}
            >
                {renderValues()}
            </div>
        </Badge>
    );
};



export const ResultBadge = ({ result, isJokerWin, isJokerRound }) => {
    const getBadgeColor = (res) => {
        if (res === 'win') return 'success';
        if (res === 'lose') return 'danger';
        return 'secondary';
    };

    const showJokerIcon = result === 'win' && (isJokerWin || (isJokerRound && isJokerWin !== false));

    return (
        <Badge color={getBadgeColor(result)} className="text-uppercase px-3 py-2 d-inline-flex align-items-center gap-2">
            {showJokerIcon && (
                <img
                    src="/joker.png"
                    alt="Joker"
                    style={{
                        width: '30px',
                        height: '30px',
                        objectFit: 'contain',
                        position: 'absolute',
                        left: '-10px',
                    }}
                />
            )}
            <span>{result || 'pending'}</span>
        </Badge>
    );
};

/**
 * Get theme-aware color class based on amount value
 * @param {number} amount - The amount value
 * @returns {string} Bootstrap theme-aware color class
 */
export const getAmountColorClass = (amount) => {
    if (amount > 0) return 'text-success'; // Green for positive amounts (theme-aware)
    if (amount < 0) return 'text-danger';  // Red for negative amounts (theme-aware)
    return 'text-muted';                    // Grey for zero amounts (theme-aware)
};

export const AmountDisplay = ({ amount, type = 'neutral' }) => {
    // Normalize amount to number
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;

    const getColorClass = () => {
        // If type is explicitly set, use it
        if (type === 'debit') return 'text-danger';
        if (type === 'credit') return 'text-success';
        // Otherwise, determine by amount value
        return getAmountColorClass(numAmount);
    };

    const getPrefix = () => {
        if (type === 'debit') return '-';
        if (type === 'credit' || numAmount > 0) return '+';
        return '';
    };

    return (
        <span className={`fw-bold ${getColorClass()}`}>
            {numAmount === 0 || !amount ? (
                <span className="text-muted">0</span>
            ) : (
                `${getPrefix()}${Math.abs(numAmount)}`
            )}
        </span>
    );
};

export const getBidHistoryColumns = () => [
    {
        name: "Round #",
        selector: row => row.roundNumber || 'N/A',
        sortable: true,
        width: "12%",
        cell: row => <span className="fw-semibold">{row.roundNumber || 'N/A'}</span>
    },
    {
        name: "Time",
        cell: row => (
            <small className="text-muted">
                {row.bidTime ? new Date(row.bidTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) : 'N/A'}
            </small>
        ),
        width: "18%"
    },
    {
        name: "Numbers Selected",
        width: "30%",
        cell: row => (
            <div className="d-flex flex-wrap gap-1">
                {row.numbersSelected?.map((num, idx) => (
                    <BidNumberBadge
                        key={idx}
                        number={num.number}
                        amount={num.amount}
                        isWinning={num.isWinningNumber}
                    />
                ))}
            </div>
        )
    },
    {
        name: "Bet Amount",
        cell: row => <AmountDisplay amount={row.totalBidAmount} type="debit" />,
        width: "12%"
    },
    {
        name: "Result",
        cell: row => (
            <ResultBadge
                result={row.result}
                isJokerWin={row.isJokerWin}
                isJokerRound={row.isJokerRound}
            />
        ),
        width: "12%"
    },
    {
        name: "Reward",
        cell: row => <AmountDisplay amount={row.reward} type="credit" />,
        width: "12%"
    },
    {
        name: "P&L",
        cell: row => <AmountDisplay amount={row.profitLoss} />,
        width: "12%"
    }
];

export const getROBidHistoryColumns = () => [
    {
        name: "Round #",
        selector: row => row.roundNumber || 'N/A',
        sortable: true,
        width: "10%",
        cell: row => <span className="fw-semibold">{row.roundNumber || 'N/A'}</span>
    },
    {
        name: "Time",
        cell: row => (
            <small className="text-muted">
                {row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) : 'N/A'}
            </small>
        ),
        width: "18%"
    },
    {
        name: "Winning",
        cell: row => (
            <div className="d-flex justify-content-center w-100">
                <Badge color="dark" className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>
                    {row.winningNumber || '-'}
                </Badge>
            </div>
        ),
        width: "8%"
    },
    {
        name: "Bets Placed",
        width: "36%",
        cell: row => (
            <div className="d-flex flex-wrap gap-1 py-1">
                {row.bets?.map((bet, idx) => (
                    <ROBetDisplay
                        key={idx}
                        bet={bet}
                        isWinningRow={row.result === 'win'}
                        winningNumber={row.winningNumber}
                    />
                ))}
            </div>
        )
    },
    {
        name: "Bet Amount",
        cell: row => <AmountDisplay amount={row.totalBidAmount} type="debit" />,
        width: "10%"
    },
    {
        name: "Result",
        cell: row => <ResultBadge result={row.result} />,
        width: "10%"
    },
    {
        name: "Reward",
        cell: row => <AmountDisplay amount={row.reward} type="credit" />,
        width: "8%"
    },
    {
        name: "P&L",
        cell: row => <AmountDisplay amount={row.profitLoss} />,
        width: "8%"
    }
];


export const InfoRow = ({ label, value, valueClass = "fw-bold", noBorder = false }) => (
    <div className={`d-flex justify-content-between align-items-center py-2 ${!noBorder ? 'border-bottom' : ''}`}>
        <span className="text-muted">{label}:</span>
        <span className={valueClass}>{value}</span>
    </div>
);

export const getFinancialOverviewConfig = (financialSummary, bidAnalysis) => [
    [
        {
            label: "Net P&L",
            value: financialSummary.netProfitLoss || 0,
            valueClass: `fw-bold ${getAmountColorClass(financialSummary.netProfitLoss || 0)}`
        },
        {
            label: "Win Rate",
            value: bidAnalysis.winRate || '0%',
            valueClass: "fw-bold text-primary"
        }
    ],
    [
        {
            label: "Total Wins",
            value: financialSummary.totalWins || 0,
            valueClass: "fw-bold"
        },
        {
            label: "Avg Bet",
            value: financialSummary.averageBetAmount || '0',
            valueClass: "fw-bold"
        }
    ],
    [
        {
            label: "Largest Win",
            value: financialSummary.largestWin || 0,
            valueClass: "fw-bold text-success"
        },
        {
            label: "Pending to Claim",
            value: financialSummary.pendingToClaim || 0,
            valueClass: "fw-bold text-danger"
        }
    ]
];

export const getROFinancialOverviewConfig = (financialSummary) => [
    [
        {
            label: "Net P&L",
            value: financialSummary.netProfitLoss || 0,
            valueClass: `fw-bold ${getAmountColorClass(financialSummary.netProfitLoss || 0)}`
        },
        {
            label: "Total Bids",
            value: financialSummary.totalBidsPlaced || 0,
            valueClass: "fw-bold text-primary"
        }
    ],
    [
        {
            label: "Total Spent",
            value: financialSummary.totalAmountSpent || 0,
            valueClass: "fw-bold text-danger"
        },
        {
            label: "Total Won",
            value: financialSummary.totalAmountWon || 0,
            valueClass: "fw-bold text-success"
        }
    ],
    [
        {
            label: "Avg Daily Bids",
            value: financialSummary.avgDailyBids || 0,
            valueClass: "fw-bold"
        },
        {
            label: "Pending to Claim",
            value: financialSummary.pendingToClaim || 0,
            valueClass: "fw-bold text-warning"
        }
    ]
];


export const getROQuickStatsConfig = (quickStats) => [
    { label: "Total Rounds", value: quickStats.totalRounds },
    { label: "Active Sessions", value: quickStats.activeSessions },
    { label: "Last Won Round", value: quickStats.lastWonRound },
    {
        label: "Most Played Bet",
        value: quickStats.mostPlayedBet,
        valueClass: "fw-bold text-primary"
    },
    {
        label: "Win/Loss Ratio",
        value: quickStats.winLossRatio,
        valueClass: "fw-bold text-success"
    }
].filter(item => item.value !== undefined && item.value !== null && item.value !== 0 && item.value !== 'N/A' && item.value !== '0%');

export const getROAdminInsightsConfig = (adminInsights) => [
    {
        label: "Total Revenue",
        value: adminInsights.totalRevenue,
        valueClass: "fw-bold text-danger"
    },
    {
        label: "Total Payout",
        value: adminInsights.totalPayout,
        valueClass: "fw-bold text-success"
    },
    {
        label: "Admin GGR",
        value: adminInsights.adminGGR,
        valueClass: "fw-bold text-primary"
    },
    {
        label: "Hold Percentage",
        value: adminInsights.yieldPercentage ? `${adminInsights.yieldPercentage}%` : null,
        valueClass: "fw-bold text-info"
    }

].filter(item => item.value !== undefined && item.value !== null && item.value !== 0 && item.value !== '0%');


export const getROSummaryCardsConfig = (financialSummary) => [
    {
        icon: "ri-wallet-line",
        title: "Account Balance",
        value: financialSummary.acBalance || 0,
        color: "primary",
        iconBg: "#e3f2fd"
    },
    {
        icon: "ri-gamepad-line",
        title: "Total Bids",
        value: financialSummary.totalBidsPlaced || 0,
        color: "info",
        iconBg: "#e0f7fa"
    },
    {
        icon: "ri-money-dollar-circle-line",
        title: "Total Spent",
        value: financialSummary.totalAmountSpent || 0,
        color: "danger",
        iconBg: "#ffebee"
    },
    {
        icon: "ri-trophy-line",
        title: "Total Won",
        value: financialSummary.totalAmountWon || 0,
        color: "success",
        iconBg: "#e8f5e9"
    }
];




export const getQuickStatsConfig = (quickStats) => [
    { label: "Total Activities", value: quickStats.totalActivities || 0 },
    { label: "Active Days", value: quickStats.activeDays || 0 },
    { label: "Avg Daily Bets", value: quickStats.averageDailyBets || '0' },
    {
        label: "Favorite Number",
        value: quickStats.favoriteNumber || 'N/A',
        valueClass: "fw-bold text-primary"
    },
    {
        label: "Luckiest Number",
        value: quickStats.luckiestNumber || 'N/A',
        valueClass: "fw-bold text-success"
    }
];

export const getAdminInsightsConfig = (adminInsights) => [
    {
        label: "Total Received",
        value: adminInsights.totalReceivedFromUser || 0,
        valueClass: "fw-bold text-danger"
    },
    {
        label: "Total Paid",
        value: adminInsights.totalPaidToUser || 0,
        valueClass: "fw-bold text-success"
    },
    {
        label: "Net Profit",
        value: adminInsights.netProfitFromUser || 0,
        valueClass: "fw-bold text-primary"
    },
    {
        label: "Profit Margin",
        value: `${adminInsights.profitMargin || '0'}%`,
        valueClass: "fw-bold text-info"
    }
];

export const tabsConfig = [
    {
        id: '1',
        icon: 'ri-history-line',
        label: 'Bid History'
    },
    {
        id: '2',
        icon: 'ri-wallet-3-line',
        label: 'Balance History'
    },
    {
        id: '3',
        icon: 'ri-bar-chart-box-line',
        label: 'Statistics'
    },
    {
        id: '4',
        icon: 'ri-gamepad-line',
        label: 'Game History'
    }
];